import * as React from 'react';
import { useCallback, useState, useMemo, memo } from 'react';
import {
  Grid,
  MenuItem,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Typography,
  TextField,
} from '@mui/material';
import { Controller } from 'react-hook-form';
import { makeStyles } from '@mui/styles';
import { isEmpty } from 'ramda';

import DriverInputs from '../../driverDetails/components/driverInputs';
import Autocomplete from '../../../components/Autocomplete';
import { PrimaryButton } from './primaryButton';
import { validateRouteNumber } from '../validators';
import {
  CREATE_ROUTE,
  MAIN_DRIVERS,
  NEED_CREATE_ROUTE_MANUALLY,
  NEXT,
  NO_OPTIONS,
  ODF,
  OTHER_DRIVERS,
  ROUTE_NUMBER,
  SECTION,
  DRIVER,
} from '../../../constants/strings';
import CheckboxIcon from '../../../components/icons/Checkbox';
import { isDriverInputDisabled } from '../model';
import { View } from '../../../components/Base';
import DriverListOption from './DriverListOption';
import { validateSectionCode } from '../../driverDetails/validators';

const useStyles = makeStyles(({ typography }) => ({
  inputs: {
    minHeight: '13rem',
    margin: '0.75rem 0',
  },
  buttons: {
    padding: 0,
  },
  inBuildConfirm: {
    margin: 0,
    alignItems: 'flex-start',
  },
  label: {
    paddingTop: typography.pxToRem(2),
    fontSize: typography.pxToRem(11),
    marginLeft: typography.pxToRem(8),
  },
  gridContainer: {
    margin: '0 -8px',
    width: 'calc(100% + 16px)',

    '& > .MuiGrid-item': {
      padding: '8px',
    },
  },
}));

const AddRouteForm = ({
  drivers,
  driver,
  vehicles,
  isSubmitting,
  handleNext,
  handleSelectDriver,
  handleSelectSection,
  handleSelectOdfDriver,
  dirty,
  sections,
  routeNumber,
  values,
  isMultiRunRoute,
  routeNumberRef,
  isRouteInBuild,
  odfDriver,
  odfDrivers,
  control,
  showMessage,
}) => {
  const styles = useStyles();

  const onNext = useCallback(() => {
    handleNext(values);
  }, [handleNext, values]);

  const [confirm, setConfirm] = useState(false);

  const onChangeConfirm = useCallback((e) => setConfirm(e.target.checked), []);

  const isCreateRouteEnable = isRouteInBuild
    ? !isSubmitting && confirm
    : !isSubmitting;

  const onSectionDropdownClick = useCallback(
    (e) => {
      e.target.value && handleSelectSection(e.target.value);
    },
    [handleSelectSection]
  );

  const onRouteNumberEnterClick = useCallback(
    (e) => {
      if (e.keyCode === 13) {
        e.preventDefault();
        onNext();
      }
      if (!/[0-9]/.test(e.key) && e.key !== 'Backspace') {
        e.preventDefault();
      }
    },
    [onNext]
  );

  const isSelectDriverDisabled = useMemo(
    () => isDriverInputDisabled(isSubmitting, isMultiRunRoute, isRouteInBuild),
    [isSubmitting, isMultiRunRoute, isRouteInBuild]
  );

  const groupByDrivers = useCallback(
    (option) => (option.isActive ? MAIN_DRIVERS : OTHER_DRIVERS),
    []
  );

  const getDriverOptionLabel = useCallback(
    (option) => <DriverListOption option={option} />,
    []
  );

  return (
    <>
      <View className={styles.inputs}>
        <Grid container spacing={2} className={styles.gridContainer}>
          <Grid item xs={4}>
            <Controller
              name="routeNumber"
              control={control}
              rules={{
                validate: validateRouteNumber,
              }}
              render={({ field, formState: { errors } }) => (
                <TextField
                  {...field}
                  label={ROUTE_NUMBER}
                  type="text"
                  variant="outlined"
                  data-testid="route-number"
                  error={!!errors.routeNumber}
                  helperText={errors.routeNumber?.message}
                  value={values.routeNumber}
                  disabled={!!routeNumber}
                  fullWidth
                  inputRef={routeNumberRef}
                  onKeyDown={onRouteNumberEnterClick}
                />
              )}
            />
          </Grid>
          {routeNumber && (
            <Grid item xs={6}>
              <Controller
                name="sectionCode"
                control={control}
                rules={{
                  validate: validateSectionCode,
                }}
                render={({ field, formState: { errors } }) => (
                  <TextField
                    {...field}
                    select
                    variant="outlined"
                    margin="normal"
                    label={SECTION}
                    fullWidth
                    data-testid="section"
                    onClick={onSectionDropdownClick}
                    error={!!errors.sectionCode}
                    helperText={errors.sectionCode?.message}
                    sx={{
                      margin: 0,
                    }}
                  >
                    {!isEmpty(sections) ? (
                      sections.map((section) => (
                        <MenuItem
                          value={section.sectionCode}
                          key={section.sectionCode}
                        >
                          {section.sectionName}
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem value="">{NO_OPTIONS}</MenuItem>
                    )}
                  </TextField>
                )}
              />
            </Grid>
          )}
        </Grid>
        {routeNumber && (
          <Grid container spacing={2} className={styles.gridContainer}>
            <Grid item xs={6}>
              <Controller
                name="odfDriverCode"
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    {...field}
                    value={odfDriver?.driverCode}
                    onChange={handleSelectOdfDriver}
                    options={odfDrivers}
                    label={ODF}
                    variant="outlined"
                    dataTestId="select-odfdriver"
                    disabled={isSelectDriverDisabled}
                    groupBy={groupByDrivers}
                    popperProps={{ placement: 'bottom-start' }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={6}>
              <Controller
                name="driverCode"
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    {...field}
                    value={driver?.driverCode}
                    onChange={handleSelectDriver}
                    options={drivers}
                    label={DRIVER}
                    variant="outlined"
                    dataTestId="select-driver"
                    disabled={isSelectDriverDisabled}
                    groupBy={groupByDrivers}
                    popperProps={{ placement: 'bottom-start' }}
                    renderOptionLabel={getDriverOptionLabel}
                  />
                )}
              />
            </Grid>
          </Grid>
        )}
        {routeNumber && driver.driverCode && (
          <DriverInputs
            vehicles={vehicles}
            control={control}
            gridClassName={styles.gridContainer}
            onShowMessage={showMessage}
          />
        )}
      </View>
      <DialogActions className={styles.buttons}>
        <Grid
          container
          spacing={2}
          justifyContent="flex-end"
          className={styles.gridContainer}
        >
          {routeNumber && isRouteInBuild && (
            <Grid item xs={8} justifyContent="flex-start">
              <FormControlLabel
                className={styles.inBuildConfirm}
                label={
                  <Typography className={styles.label}>
                    {NEED_CREATE_ROUTE_MANUALLY}
                  </Typography>
                }
                control={
                  <Checkbox
                    disableRipple
                    checked={confirm}
                    onChange={onChangeConfirm}
                    icon={<CheckboxIcon />}
                    checkedIcon={<CheckboxIcon checked />}
                  />
                }
              />
            </Grid>
          )}
          <Grid item xs={4} container justifyContent="flex-end">
            {routeNumber ? (
              <PrimaryButton
                type="submit"
                key="Submit"
                disabled={!isCreateRouteEnable}
                title={CREATE_ROUTE}
              />
            ) : (
              <PrimaryButton
                key="Next"
                disabled={!dirty}
                title={NEXT}
                handleClick={onNext}
              />
            )}
          </Grid>
        </Grid>
      </DialogActions>
    </>
  );
};

export default memo(AddRouteForm);
